import { getAPIUrl } from "config/api";

/**
 * Checks if a URL is from our S3 bucket and should be proxied.
 */
function shouldProxyUrl(url: string): boolean {
  const s3Hosts = [
    "prod-clearhealthresources.s3.amazonaws.com",
    "dev-clearhealthresources.s3.amazonaws.com",
    "staging-clearhealthresources.s3.amazonaws.com",
  ];

  try {
    const parsedUrl = new URL(url);
    return s3Hosts.includes(parsedUrl.hostname);
  } catch {
    return false;
  }
}

/**
 * Gets the proxied URL for an S3 image to avoid CORS issues.
 * The backend proxy fetches the image server-side where CORS doesn't apply.
 */
function getProxiedImageUrl(url: string): string {
  const apiUrl = getAPIUrl();
  const baseUrl = apiUrl.endsWith("/") ? apiUrl : `${apiUrl}/`;
  return `${baseUrl}image-proxy?url=${encodeURIComponent(url)}`;
}

/**
 * Converts an image URL to a base64 data URL using canvas.
 * For S3 images, routes through our backend proxy to avoid CORS issues
 * that can occur with certain browser extensions or network configurations.
 *
 * @param url - The URL of the image to convert
 * @param fallbackUrl - Optional fallback URL if the primary URL fails
 * @returns Promise resolving to the base64 data URL, or the fallback/empty string on failure
 */
export function imageUrlToBase64(
  url: string | null | undefined,
  fallbackUrl?: string
): Promise<string> {
  if (!url) {
    return Promise.resolve(fallbackUrl || "");
  }

  // Use proxy for S3 URLs to avoid CORS issues
  const imageUrl = shouldProxyUrl(url) ? getProxiedImageUrl(url) : url;

  return new Promise((resolve) => {
    const img = new Image();
    // Always need crossOrigin to allow canvas drawing
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };

    img.onerror = () => {
      console.warn("Failed to load image for base64 conversion:", url);
      resolve(fallbackUrl || "");
    };

    img.src = imageUrl;
  });
}
